import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import { formatDateTime } from '../../../helpers'
import { IBlackList } from '../../../interfaces/BlackList/IBlackList'
import { BlackListService } from '../../../services/BlackList/BlackListService'
import BlackListPageModal from './BlackListPageModal'

export default function BlackListPage() {
  const service = new BlackListService()
  const toast = useToast()
  const [blackListed, setBlackListed] = useState<IBlackList[]>([])
  const [search, setSearch] = useState('')
  const [filtered, setFiltered] = useState<IBlackList[]>([])
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editing, setEditing] = useState<IBlackList | null>(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState<IBlackList | null>(null)
  const [isUpdating, setIsUpdating] = useState<number[]>([])
  const cancelRef = useRef(null)

  useEffect(() => {
    loadBlackListedMails()
  }, [])

  useEffect(() => {
    setFiltered(blackListed.filter((c) => c.email.toLowerCase().includes(search.toLowerCase())))
  }, [search, blackListed])

  const loadBlackListedMails = async () => {
    setBlackListed((await service.list())?.data || [])
  }

  const handleUpdateEnabled = async (enabled: boolean, id: number) => {
    setIsUpdating((prev) => [...prev, id])

    try {
      if (enabled) {
        await service.enable(id)
      } else {
        await service.disable(id)
      }

      toast({
        title: 'Sucesso',
        description: `E-mail ${enabled ? 'bloqueado' : 'desbloqueado'} com sucesso`,
        status: 'success',
        duration: 3000,
        isClosable: true
      })

      setBlackListed((prev) => prev.map((item) => (item.id === id ? { ...item, is_active: enabled } : item)))
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível atualizar o status',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setIsUpdating((prev) => prev.filter((i) => i !== id))
    }
  }

  const handleDelete = async () => {
    if (!deleting) return

    try {
      await service.delete(deleting.id)

      toast({
        title: 'Excluído com sucesso',
        description: `O e-mail ${deleting.email} foi removido da blacklist.`,
        status: 'success',
        duration: 3000,
        isClosable: true
      })

      setBlackListed((prev) => prev.filter((item) => item.id !== deleting.id))
    } catch (error) {
      toast({
        title: 'Erro ao excluir',
        description: 'Não foi possível excluir o e-mail da blacklist.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setIsDeleteOpen(false)
      setDeleting(null)
    }
  }

  function truncateString(text: string | undefined | null, maxLength = 25): string {
    if (typeof text !== 'string') {
      return ''
    }

    if (text.length <= maxLength) {
      return text
    }

    return text.substring(0, maxLength) + '...'
  }

  return (
    <>
      <Heading as="h1" size="lg" mb="10" textTransform="uppercase">
        E-mails bloqueados
      </Heading>
      <Flex flex="1" justifyContent="flex-end" alignItems="center">
        <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal" p={0} m={0} mr={2}>
          Pesquisar
        </FormLabel>
        <Input
          type="search"
          size="sm"
          variant="outline"
          id="search"
          w="200px"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Flex>
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>E-mail</Th>
              <Th>Motivo</Th>
              <Th>Criado por</Th>
              <Th>Criado em</Th>
              <Th>Ativo</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtered.length === 0 ? (
              <Tr>
                <Td colSpan={6} textAlign="center">
                  Nenhum e-mail encontrado
                </Td>
              </Tr>
            ) : (
              filtered
                .sort((a, b) => a.id - b.id)
                .map((blackList) => (
                  <Tr key={blackList.id}>
                    <Td>{blackList.id}</Td>
                    <Td>{blackList.email}</Td>
                    <Td>
                      <Tooltip label={blackList.reason || ''} placement="top" hasArrow>
                        <span>{truncateString(blackList.reason)}</span>
                      </Tooltip>
                    </Td>
                    <Td>{blackList.created_by || 'N/A'}</Td>
                    <Td>{blackList.created_at ? formatDateTime(blackList.created_at) : 'N/A'}</Td>
                    <Td>
                      <Switch
                        isChecked={blackList.is_active}
                        colorScheme="green"
                        size="sm"
                        onChange={(e) => handleUpdateEnabled(e.target.checked, blackList.id)}
                        isDisabled={isUpdating.includes(blackList.id)}
                        title={blackList.is_active ? 'Desativar' : 'Ativar'}
                      />
                    </Td>
                    <Td>
                      <Tooltip label="Editar">
                        <IconButton
                          aria-label="Editar"
                          icon={<i className="lni lni-pencil" />}
                          colorScheme="blue"
                          size="sm"
                          mr={2}
                          onClick={() => {
                            setEditing(blackList)
                            setIsFormOpen(true)
                          }}
                        />
                      </Tooltip>
                      <Tooltip label="Excluir">
                        <IconButton
                          aria-label="Excluir"
                          icon={<i className="lni lni-trash-can" />}
                          size="sm"
                          colorScheme="red"
                          onClick={() => {
                            setDeleting(blackList)
                            setIsDeleteOpen(true)
                          }}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                ))
            )}
          </Tbody>

          <AlertDialog
            isOpen={isDeleteOpen}
            leastDestructiveRef={cancelRef}
            onClose={() => setIsDeleteOpen(false)}
            isCentered
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  Confirmar exclusão
                </AlertDialogHeader>

                <AlertDialogBody>
                  Tem certeza que deseja remover o e-mail <strong>{deleting?.email}</strong> da blacklist?
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={() => setIsDeleteOpen(false)}>
                    Cancelar
                  </Button>
                  <Button colorScheme="red" onClick={handleDelete} ml={3}>
                    Excluir
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        </Table>
      </TableContainer>
      <BlackListPageModal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          loadBlackListedMails()
        }}
        data={editing}
      />

      <Tooltip label="Adicionar " aria-label="Adicionar " placement="left">
        <Button
          colorScheme="magenta"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={() => {
            setEditing(null)
            setIsFormOpen(true)
          }}
        >
          <i className="lni lni-plus"></i>
        </Button>
      </Tooltip>
    </>
  )
}
